import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { getBrandPillStyle } from "@/lib/brandColors";
import { extractErrorMessage } from "@/lib/utils";
import { Plus, Trash2, AlertCircle, CheckCircle2, ArrowLeft, Save } from "lucide-react";
import { SellerContributionCard } from "@/components/SellerContributionCard";
import { useApprovalThreshold, computeApprovalTrigger } from "@/lib/approvalTrigger";
import { computeSlabValidation, useApplicableSlab } from "@/lib/slabValidation";
import { toast } from "sonner";
import { SearchableSelect } from "@/components/ui/searchable-select";
import MultiSelectChips from "@/components/MultiSelectChips";
import { useRemarkCategories } from "@/hooks/useRemarkCategories";
import { useWarehouseAvailability, computeInventoryViolations } from "@/hooks/useInventoryRemaining";
import { useActiveWarehouses } from "@/hooks/useActiveWarehouses";
import { currentUserIsAdmin } from "@/components/mcf/mcfUtils";

// Everything editable except Pickup Warehouse — used to detect an address-only edit.
const editSnapshot = (v) => JSON.stringify([v.items, v.freebies, v.orderDate, v.approvalOverride, v.approvalRemarks, v.remarkCategories, v.quotationType, v.sellerId, v.transporterName || null]);

export default function EditQuotation() {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [discountSlabs, setDiscountSlabs] = useState([]);
  const [quotation, setQuotation] = useState(null);
  const [orderDate, setOrderDate] = useState(""); // Editable order date
  const [saleItems, setSaleItems] = useState([]);
  const [freebieItems, setFreebieItems] = useState([]);
  const [approvalOverride, setApprovalOverride] = useState(false);
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [remarkCategories, setRemarkCategories] = useState([]);
  const allRemarkCategories = useRemarkCategories();
  const [transporters, setTransporters] = useState([]);
  const [selectedTransporter, setSelectedTransporter] = useState(null);
  const { warehouses: activeWarehouses, options: warehouseOptions, loaded: warehousesLoaded, refresh: refreshWarehouses, isActive: isWarehouseActive } = useActiveWarehouses();
  const [pickupWarehouseId, setPickupWarehouseId] = useState("");
  const [quotationType, setQuotationType] = useState("offline");
  const [loading, setLoading] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [selectedSellerId, setSelectedSellerId] = useState("");
  const originalSnapshot = useRef(null);

  useEffect(() => {
    fetchData();
  }, [quotationId]);

  const fetchData = async () => {
    try {
      const [productsRes, slabsRes, quotationRes, transportersRes, sellersRes] = await Promise.all([
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
        axios.get(`${API}/discount-slabs`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
        axios.get(`${API}/quotations/${quotationId}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
        axios.get(`${API}/transporters`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
        axios.get(`${API}/sellers`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
      ]);
      setProducts(productsRes.data);
      setDiscountSlabs(slabsRes.data);
      setTransporters(transportersRes.data);
      setSellers(sellersRes.data || []);
      const q = quotationRes.data;
      setQuotation(q);
      setSelectedSellerId(q.seller_id || "");
      setPickupWarehouseId(q.pickup_warehouse_id || "");
      // Set order date in YYYY-MM-DD format for the date input
      const existingDate = new Date(q.order_date);
      setOrderDate(existingDate.toISOString().split('T')[0]);
      setSaleItems(q.items || []);
      setFreebieItems(q.freebies || []);
      setApprovalOverride(q.approval_override || false);
      setApprovalRemarks(q.approval_remarks || "");
      setRemarkCategories(Array.isArray(q.remark_categories) ? q.remark_categories : []);
      setQuotationType(q.quotation_type || "offline");
      originalSnapshot.current = editSnapshot({
        items: q.items || [], freebies: q.freebies || [], orderDate: existingDate.toISOString().split('T')[0],
        approvalOverride: q.approval_override || false, approvalRemarks: q.approval_remarks || "",
        remarkCategories: Array.isArray(q.remark_categories) ? q.remark_categories : [],
        quotationType: q.quotation_type || "offline", sellerId: q.seller_id || "", transporterName: q.transporter_name,
      });
      // Find and set existing transporter
      if (q.transporter_name) {
        const existingTransporter = transportersRes.data.find(t => t.transporter_name === q.transporter_name);
        setSelectedTransporter(existingTransporter || { transporter_name: q.transporter_name, transporter_id: q.transporter_code, id: q.transporter_id });
      }
    } catch (error) {
      toast.error("Error fetching data");
    }
  };

  const addSaleItem = () => {
    setSaleItems([...saleItems, {
      product_id: "",
      product_name: "",
      sku_id: "",
      brand: "",
      mrp: 0,
      quantity: 1,
      discount: 0,
      item_type: "sale",
      exclude_from_discount: false,
      total_mrp: 0,
      invoice_value: 0
    }]);
  };

  const addFreebieItem = () => {
    setFreebieItems([...freebieItems, {
      product_id: "",
      product_name: "",
      sku_id: "",
      brand: "",
      mrp: 0,
      quantity: 1,
      discount: 100,
      item_type: "freebie",
      exclude_from_discount: false,
      total_mrp: 0,
      invoice_value: 0
    }]);
  };

  const removeSaleItem = (index) => {
    setSaleItems(saleItems.filter((_, i) => i !== index));
  };

  const removeFreebieItem = (index) => {
    setFreebieItems(freebieItems.filter((_, i) => i !== index));
  };

  const updateSaleItem = (index, field, value) => {
    const updated = [...saleItems];
    const item = updated[index];
    
    if (field === "product_id") {
      const product = products.find(p => p.id === value);
      if (product) {
        item.product_id = product.id;
        item.product_name = product.product_name;
        item.sku_id = product.sku_id;
        item.brand = product.brand;
        item.mrp = product.mrp;
        item.exclude_from_discount = product.exclude_from_discount || false;
      }
    } else if (field === "quantity") {
      item.quantity = value === "" ? 0 : (parseInt(value) || 0);
    } else if (field === "discount") {
      item.discount = value === "" ? 0 : (parseFloat(value) || 0);
    }
    
    item.total_mrp = item.mrp * item.quantity;
    item.invoice_value = item.total_mrp * (1 - item.discount / 100);
    
    setSaleItems(updated);
  };

  const updateFreebieItem = (index, field, value) => {
    const updated = [...freebieItems];
    const item = updated[index];
    
    if (field === "product_id") {
      const product = products.find(p => p.id === value);
      if (product) {
        item.product_id = product.id;
        item.product_name = product.product_name;
        item.sku_id = product.sku_id;
        item.brand = product.brand;
        item.mrp = product.mrp;
        item.exclude_from_discount = product.exclude_from_discount || false;
      }
    } else if (field === "quantity") {
      item.quantity = value === "" ? 0 : (parseInt(value) || 0);
    }
    
    item.total_mrp = item.mrp * item.quantity;
    item.invoice_value = 0;
    item.discount = 100;
    
    setFreebieItems(updated);
  };

  // Memoized once per products load — avoids rebuilding option objects for every item row on
  // every keystroke (was a major edit-screen lag source with large product catalogs).
  const productOptions = useMemo(
    () => (products || []).map((p) => ({
      value: p.id,
      label: p.product_name,
      searchTerms: [p.product_name, p.sku_id, p.brand].filter(Boolean),
    })),
    [products]
  );

  // NEW DISCOUNT ENGINE - Calculate totals with SKU-level exclusion logic
  const calculateTotals = () => {
    // Total values (ALL items)
    const totalMrpValue = saleItems.reduce((sum, item) => sum + item.total_mrp, 0);
    const totalInvoiceValue = saleItems.reduce((sum, item) => sum + item.invoice_value, 0);
    const totalDiscountValue = totalMrpValue - totalInvoiceValue;
    const freebiesValue = freebieItems.reduce((sum, item) => sum + item.total_mrp, 0);
    
    // ELIGIBLE values (only items where exclude_from_discount = false)
    const eligibleSaleItems = saleItems.filter(item => !item.exclude_from_discount);
    const eligibleFreebieItems = freebieItems.filter(item => !item.exclude_from_discount);
    
    const eligibleMrp = eligibleSaleItems.reduce((sum, item) => sum + item.total_mrp, 0) +
                        eligibleFreebieItems.reduce((sum, item) => sum + item.total_mrp, 0);
    const eligibleInvoiceValue = eligibleSaleItems.reduce((sum, item) => sum + item.invoice_value, 0);
    
    // EFFECTIVE DISCOUNT % = ((Eligible MRP - Invoice Value) / Eligible MRP) × 100
    const effectiveDiscount = eligibleMrp > 0 
      ? ((eligibleMrp - eligibleInvoiceValue) / eligibleMrp) * 100 
      : 0;
    
    // ALLOWED DISCOUNT % - NEW LOGIC
    let allowedDiscount = 0;
    
    if (eligibleInvoiceValue > 0) {
      // Step 2: Determine Base Discount from slab
      let baseDiscount = 0;
      if (eligibleInvoiceValue <= 300000) baseDiscount = 61;
      else if (eligibleInvoiceValue <= 500000) baseDiscount = 62;
      else if (eligibleInvoiceValue <= 1500000) baseDiscount = 62.5;
      else if (eligibleInvoiceValue <= 5000000) baseDiscount = 63;
      else if (eligibleInvoiceValue <= 10000000) baseDiscount = 64.2;
      else baseDiscount = 65.2;
      
      // Step 3: Calculate brand-wise invoice contribution (only eligible items)
      const brandInvoice = {
        "Wellcore": 0,
        "Dynamite": 0,
        "Okami": 0,
        "YouWeFit": 0,
        "Zero Sugar": 0,
        "OSMO": 0
      };
      
      eligibleSaleItems.forEach(item => {
        if (item.brand in brandInvoice) {
          brandInvoice[item.brand] += item.invoice_value;
        }
      });
      
      // Step 4: Additional Discount (+1% per qualifying brand)
      // Applicable brands: Dynamite, YouWeFit, Okami, ZeroSugar
      // Condition: Brand Invoice Value >= 25% of Total Invoice Value
      let additionalDiscount = 0;
      const qualifyingBrands = ["Dynamite", "YouWeFit", "Okami", "Zero Sugar"];
      const threshold = eligibleInvoiceValue * 0.25;
      
      qualifyingBrands.forEach(brand => {
        if (brandInvoice[brand] >= threshold) {
          additionalDiscount += 1;
        }
      });
      
      // Step 5: Total Allowed Discount
      allowedDiscount = baseDiscount + additionalDiscount;
      
      // Step 6: Apply STRICT CAP of 63%
      allowedDiscount = Math.min(allowedDiscount, 63);
    }
    
    const finalStatus = effectiveDiscount <= allowedDiscount ? "valid" : "over_discount";

    // BRAND-WISE CALCULATIONS — mirrors Create Quotation logic so the Brand-wise discount panel
    // shown below the Summary matches exactly. Includes eligible freebies in the MRP base,
    // excludes any line with exclude_from_discount=true (shakers / carry bags / etc.).
    const brandStats = {};
    const brands = ["Wellcore", "Dynamite", "Okami", "YouWeFit", "Zero Sugar", "OSMO"];
    brands.forEach((brand) => {
      const brandEligibleSaleItems = saleItems.filter(
        (item) => item.brand === brand && !item.exclude_from_discount
      );
      const brandEligibleFreebieItems = freebieItems.filter(
        (item) => item.brand === brand && !item.exclude_from_discount
      );
      const brandSaleMrp = brandEligibleSaleItems.reduce((sum, item) => sum + item.total_mrp, 0);
      const brandFreebieMrp = brandEligibleFreebieItems.reduce((sum, item) => sum + item.total_mrp, 0);
      const brandTotalMrp = brandSaleMrp + brandFreebieMrp;
      const brandInvoice = brandEligibleSaleItems.reduce((sum, item) => sum + item.invoice_value, 0);
      const brandDiscount = brandTotalMrp > 0 ? ((brandTotalMrp - brandInvoice) / brandTotalMrp) * 100 : 0;
      if (brandTotalMrp > 0) {
        brandStats[brand] = {
          mrp: brandTotalMrp,
          saleMrp: brandSaleMrp,
          freebieMrp: brandFreebieMrp,
          invoice: brandInvoice,
          discount: brandDiscount,
        };
      }
    });

    return {
      totalMrpValue,
      totalInvoiceValue,
      totalDiscountValue,
      freebiesValue,
      eligibleMrp,
      eligibleInvoiceValue,
      effectiveDiscount,
      allowedDiscount,
      finalStatus,
      brandStats,
    };
  };

  const totals = calculateTotals();
  const approvalThreshold = useApprovalThreshold();
  const approvalTrigger = computeApprovalTrigger(totals.effectiveDiscount, totals.brandStats, approvalThreshold);

  // ─── Real-time inventory allocation validation ───
  // Exclude this quotation's own contribution so re-saving with the same qty
  // doesn't block itself. Violations are shown inline even on drafts (informational),
  // but only DISABLE Save when editing a non-draft (final) quotation.
  const { available: availableMap, hasInventory: hasWarehouseInventory, warehouseType } =
    useWarehouseAvailability(pickupWarehouseId, orderDate, quotationId);
  const savedWarehouseInactive = warehousesLoaded && !!pickupWarehouseId && !isWarehouseActive(pickupWarehouseId);
  const { violationsBySku: inventoryViolations, hasViolations: hasInventoryViolation } =
    computeInventoryViolations(saleItems, freebieItems, availableMap, hasWarehouseInventory);

  // ─── Slab Intelligence-driven brand-wise discount validation (real-time) ───
  const slabInfo = useApplicableSlab(orderDate);
  const slabVal = computeSlabValidation({
    brandStats: totals.brandStats,
    totalInvoiceValue: totals.totalInvoiceValue,
    slabs: slabInfo.slabs,
  });
  const slabRequiresApproval = slabVal.requiresApproval;
  const slabDiscountOver = slabRequiresApproval;
  // Approval can also be triggered by the seller revenue contribution limit (reported by the
  // SellerContributionCard). Any trigger means a FINAL edit must go via Save-as-Draft.
  const [contribExceeds, setContribExceeds] = useState(false);
  const approvalRequired = slabRequiresApproval || contribExceeds;
  const blockFinalSave = quotation && !quotation.is_draft && (hasInventoryViolation || approvalRequired);

  const isAddressOnlyEdit = () =>
    currentUserIsAdmin() && !!originalSnapshot.current && !!pickupWarehouseId &&
    pickupWarehouseId !== quotation?.pickup_warehouse_id && isWarehouseActive(pickupWarehouseId) &&
    editSnapshot({
      items: saleItems, freebies: freebieItems, orderDate, approvalOverride, approvalRemarks, remarkCategories,
      quotationType, sellerId: selectedSellerId || quotation.seller_id, transporterName: selectedTransporter?.transporter_name,
    }) === originalSnapshot.current;

  // Admin Pickup Address (warehouse) only change: dedicated endpoint, no approval/draft/lock effects.
  const savePickupOnly = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/quotations/${quotationId}/pickup-warehouse`, { pickup_warehouse_id: pickupWarehouseId });
      toast.success("Pickup Address updated — approval status and other details unchanged");
      navigate(quotation.is_draft ? "/drafts" : `/quotations/${quotationId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update Pickup Address");
    } finally {
      setLoading(false);
    }
  };

  const addressOnlyEdit = quotation ? isAddressOnlyEdit() : false;

  const handleSave = async (saveAsDraft = false) => {
    // Pickup Warehouse is mandatory for every quotation (draft & final).
    if (!pickupWarehouseId) {
      toast.error("Please select a Pickup Warehouse");
      return;
    }
    if (savedWarehouseInactive) {
      toast.error("The saved Pickup Warehouse is inactive. Please select an active warehouse before saving.");
      return;
    }
    if (isAddressOnlyEdit()) {
      await savePickupOnly();
      return;
    }
    // When Slab Intelligence flags this quotation for approval, Remarks + at least one Remark
    // Category are mandatory (draft & final) so it can be submitted for Manager Approval.
    if (slabDiscountOver) {
      if (!approvalRemarks.trim()) {
        toast.error("Please add Remarks explaining why this quotation needs manager approval.");
        return;
      }
      if (remarkCategories.length === 0) {
        toast.error("Please select at least one Remark Category.");
        return;
      }
    }
    // Final-save (not Save-as-Draft) gates for a quotation that is currently FINAL.
    if (!saveAsDraft && !quotation.is_draft) {
      if (saleItems.length === 0) {
        toast.error("Please add at least one sale item");
        return;
      }
      
      if (!selectedTransporter) {
        toast.error("Transporter is mandatory");
        return;
      }

      // Approval required — a finalized quotation cannot be saved directly. The user must use
      // the "Save as Draft" action (which routes it through the existing approval workflow).
      if (approvalRequired) {
        toast.error("Manager approval required — click \"Save as Draft\" to move this quotation to Drafts and submit for approval.");
        return;
      }

      if (hasInventoryViolation) {
        toast.error("Inventory not available for one or more SKUs. Adjust quantities or keep as Draft.");
        return;
      }
    }
    
    setLoading(true);
    try {
      const payload = {
        items: saleItems,
        freebies: freebieItems,
        order_date: orderDate,
        total_mrp_value: totals.totalMrpValue,
        total_invoice_value: totals.totalInvoiceValue,
        total_discount_value: totals.totalDiscountValue,
        freebies_value: totals.freebiesValue,
        effective_discount: totals.effectiveDiscount,
        allowed_discount: totals.allowedDiscount,
        approval_override: approvalOverride,
        approval_remarks: approvalRemarks,
        remarks: approvalRemarks, // Single source of truth — frontend exposes only Approval Remarks now.
        remark_categories: remarkCategories,
        final_status: totals.finalStatus,
        transporter_id: selectedTransporter?.id || null,
        transporter_name: selectedTransporter?.transporter_name || null,
        transporter_code: selectedTransporter?.transporter_id || null,
        quotation_type: quotationType || "offline",
        seller_id: selectedSellerId || quotation.seller_id,
        pickup_warehouse_id: pickupWarehouseId,
      };
      // Save-as-Draft: converts a finalized quotation to a Draft (same Quotation ID), removing it
      // from the Quotations Log and routing it through the existing Draft approval workflow.
      if (saveAsDraft) {
        payload.is_draft = true;
      }
      
      await axios.put(`${API}/quotations/${quotationId}/update`, payload, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      if (saveAsDraft) {
        toast.success("Saved as Draft — moved to Drafts. Submit it for Manager Approval from the Drafts tab.");
        navigate(`/drafts`);
      } else {
        toast.success("Quotation updated successfully");
        navigate(`/quotations/${quotationId}`);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error updating quotation"));
    } finally {
      setLoading(false);
    }
  };

  if (!quotation) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate(`/quotations/${quotationId}`)} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="edit-quotation-title">Edit Quotation</h1>
            <p className="text-muted-foreground mt-1">{quotation.quotation_id}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {quotation && !quotation.is_draft && approvalRequired && (
            <Button
              onClick={() => handleSave(true)}
              disabled={loading}
              variant="outline"
              data-testid="save-as-draft-button"
              title="Move this quotation to Drafts and submit for Manager Approval"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? "Saving..." : "Save as Draft"}
            </Button>
          )}
          <Button
            onClick={() => handleSave(false)}
            disabled={loading || (blockFinalSave && !addressOnlyEdit)}
            className="bg-accent hover:bg-accent/90 text-white"
            data-testid="save-quotation-button"
            title={blockFinalSave && !addressOnlyEdit
              ? (approvalRequired
                  ? "Manager approval required — use \"Save as Draft\""
                  : "Resolve inventory shortages before saving the final quotation")
              : undefined}
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quotation Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="order-date">Order Date *</Label>
              <Input 
                id="order-date"
                type="date" 
                value={orderDate} 
                onChange={(e) => setOrderDate(e.target.value)}
                required
                data-testid="edit-order-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Quotation ID</Label>
              <Input value={quotation.quotation_id} disabled className="font-mono" />
            </div>
            <div className="space-y-2">
              <Label>Seller *</Label>
              <SearchableSelect
                value={selectedSellerId}
                onValueChange={(v) => setSelectedSellerId(v)}
                options={(sellers || []).map(s => ({
                  value: s.id,
                  label: s.seller_code ? `${s.seller_name} (${s.seller_code})` : s.seller_name,
                  searchTerms: [s.seller_name, s.seller_code, s.gstin, s.city, s.state].filter(Boolean),
                }))}
                placeholder="Select seller"
                searchPlaceholder="Search by name, code, GSTIN, city…"
                emptyMessage="No matching sellers"
                data-testid="edit-quotation-seller-select"
              />
              {(() => {
                const sel = sellers.find(s => s.id === selectedSellerId);
                if (!sel) return null;
                const changed = selectedSellerId !== quotation.seller_id;
                return (
                  <div className={`text-[11px] rounded-md px-2 py-1.5 border ${changed ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-600"}`} data-testid="edit-quotation-seller-summary">
                    {changed && <div className="font-semibold mb-0.5">Seller will be updated on Save:</div>}
                    <div><span className="font-medium">{sel.seller_name}</span>{sel.seller_code ? ` · ${sel.seller_code}` : ""}</div>
                    {sel.gstin && <div>GSTIN: {sel.gstin}</div>}
                    {(sel.contact_number || sel.email) && (
                      <div>{sel.contact_number ? `📞 ${sel.contact_number}` : ""}{sel.contact_number && sel.email ? " · " : ""}{sel.email || ""}</div>
                    )}
                    {sel.shipping_address && <div className="truncate" title={sel.shipping_address}>Ship: {sel.shipping_address}</div>}
                  </div>
                );
              })()}
            </div>
          </div>
          
          {/* Quotation Type + Pickup Warehouse — placed after Seller, before Transporter */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-quotation-type">Quotation Type *</Label>
              <Select value={quotationType} onValueChange={setQuotationType}>
                <SelectTrigger id="edit-quotation-type" data-testid="edit-quotation-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="offline" data-testid="edit-quotation-type-option-offline">Offline</SelectItem>
                  <SelectItem value="online" data-testid="edit-quotation-type-option-online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Pickup Warehouse *</Label>
              <SearchableSelect
                options={warehouseOptions}
                onOpenChange={(o) => o && refreshWarehouses()}
                value={pickupWarehouseId}
                onValueChange={setPickupWarehouseId}
                placeholder="Select warehouse..."
                searchPlaceholder="Type to search..."
                data-testid="edit-pickup-warehouse-select"
              />
              {(() => { const w = activeWarehouses.find((x) => x.id === pickupWarehouseId); return w ? (
                <p className="text-xs text-muted-foreground" data-testid="edit-pickup-warehouse-address">{w.address || "No pickup address configured in Warehouse Master"}</p>
              ) : null; })()}
              {savedWarehouseInactive && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-2 py-1.5" data-testid="edit-pickup-warehouse-inactive-warning">
                  {pickupWarehouseId === quotation?.pickup_warehouse_id ? `Saved warehouse “${quotation?.pickup_warehouse_name || "Unknown"}”` : "The selected warehouse"} is inactive or no longer exists in Warehouse Master. Select an active warehouse before saving.
                </p>
              )}
              {pickupWarehouseId && warehouseType === "fbw_inventory" && !hasWarehouseInventory && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-2 py-1.5" data-testid="edit-pickup-warehouse-no-stock-note">
                  No live stock recorded for this MCF warehouse yet — stock validation starts once GRN is received on a stock transfer.
                </p>
              )}
            </div>
          </div>
          
          {/* Transporter Selection */}
          <div className="space-y-2">
            <Label>Transporter *</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <SearchableSelect
                  options={transporters.map(t => ({
                    value: t.id,
                    label: `${t.transporter_name}${t.transporter_id ? ` (${t.transporter_id})` : ''}`,
                    searchTerms: [t.transporter_name, t.transporter_id]
                  }))}
                  value={selectedTransporter?.id || ""}
                  onValueChange={(val) => {
                    const transporter = transporters.find(t => t.id === val);
                    setSelectedTransporter(transporter || null);
                  }}
                  placeholder="Select transporter..."
                  searchPlaceholder="Type to search..."
                  data-testid="edit-transporter-select"
                />
              </div>
              {selectedTransporter && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedTransporter(null)}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Sale Items</CardTitle>
          <Button onClick={addSaleItem} size="sm" className="bg-accent hover:bg-accent/90 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Product</TableHead>
                  <TableHead>SKU ID</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>MRP</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead>Disc %</TableHead>
                  <TableHead>Total MRP</TableHead>
                  <TableHead>Invoice Val</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {saleItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No items added. Click "Add Item" to start.
                    </TableCell>
                  </TableRow>
                ) : (
                  saleItems.map((item, index) => (
                    <TableRow key={item.product_id || `sale-item-${index}`}>
                      <TableCell>
                        <SearchableSelect
                          value={item.product_id}
                          onValueChange={(value) => updateSaleItem(index, "product_id", value)}
                          options={productOptions}
                          placeholder="Select"
                          searchPlaceholder="Search product / SKU..."
                          className="w-full"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.sku_id}</TableCell>
                      <TableCell>{(() => { const p = getBrandPillStyle(item.brand); return <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: p.bg, color: p.text }}>{item.brand}</span>; })()}</TableCell>
                      <TableCell className="font-mono">₹{item.mrp}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.quantity || ""}
                          onChange={(e) => updateSaleItem(index, "quantity", e.target.value)}
                          className={`w-20 ${inventoryViolations[item.sku_id] ? "border-destructive ring-1 ring-destructive/40 focus-visible:ring-destructive" : ""}`}
                          placeholder="Qty"
                          data-testid={`edit-sale-quantity-${index}`}
                        />
                        {inventoryViolations[item.sku_id] && (
                          <p
                            className="text-xs text-destructive mt-1 leading-tight whitespace-nowrap"
                            data-testid={`edit-sale-inventory-error-${index}`}
                          >
                            Insufficient inventory. Available: {inventoryViolations[item.sku_id].remaining} units
                          </p>
                        )}
                      </TableCell>
                      <TableCell data-testid={`edit-sale-available-${index}`}>
                        {item.sku_id && hasWarehouseInventory ? (
                          <span className={`font-mono text-sm ${inventoryViolations[item.sku_id] ? "text-destructive font-semibold" : "text-green-600"}`}>
                            {availableMap[item.sku_id] ?? 0}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.discount || ""}
                          onChange={(e) => updateSaleItem(index, "discount", e.target.value)}
                          className="w-20"
                          placeholder="Disc %"
                          data-testid={`edit-sale-discount-${index}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono">₹{item.total_mrp.toFixed(2)}</TableCell>
                      <TableCell className="font-mono font-semibold">₹{item.invoice_value.toFixed(2)}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => removeSaleItem(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Freebies (100% Discount)</CardTitle>
          <Button onClick={addFreebieItem} size="sm" variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Freebie
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Product</TableHead>
                  <TableHead>SKU ID</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>MRP</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead>Total MRP</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {freebieItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No freebies added.
                    </TableCell>
                  </TableRow>
                ) : (
                  freebieItems.map((item, index) => (
                    <TableRow key={item.product_id || `freebie-item-${index}`}>
                      <TableCell>
                        <SearchableSelect
                          value={item.product_id}
                          onValueChange={(value) => updateFreebieItem(index, "product_id", value)}
                          options={productOptions}
                          placeholder="Select"
                          searchPlaceholder="Search product / SKU..."
                          className="w-full"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.sku_id}</TableCell>
                      <TableCell>{(() => { const p = getBrandPillStyle(item.brand); return <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: p.bg, color: p.text }}>{item.brand}</span>; })()}</TableCell>
                      <TableCell className="font-mono">₹{item.mrp}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.quantity || ""}
                          onChange={(e) => updateFreebieItem(index, "quantity", e.target.value)}
                          className={`w-20 ${inventoryViolations[item.sku_id] ? "border-destructive ring-1 ring-destructive/40 focus-visible:ring-destructive" : ""}`}
                          placeholder="Qty"
                          data-testid={`edit-freebie-quantity-${index}`}
                        />
                        {inventoryViolations[item.sku_id] && (
                          <p
                            className="text-xs text-destructive mt-1 leading-tight whitespace-nowrap"
                            data-testid={`edit-freebie-inventory-error-${index}`}
                          >
                            Insufficient inventory. Available: {inventoryViolations[item.sku_id].remaining} units
                          </p>
                        )}
                      </TableCell>
                      <TableCell data-testid={`edit-freebie-available-${index}`}>
                        {item.sku_id && hasWarehouseInventory ? (
                          <span className={`font-mono text-sm ${inventoryViolations[item.sku_id] ? "text-destructive font-semibold" : "text-green-600"}`}>
                            {availableMap[item.sku_id] ?? 0}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono">₹{item.total_mrp.toFixed(2)}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => removeFreebieItem(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Summary & Validation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total MRP</p>
              <p className="text-2xl font-bold font-mono">₹{totals.totalMrpValue.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Invoice Value</p>
              <p className="text-2xl font-bold font-mono">₹{totals.totalInvoiceValue.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Discount Value</p>
              <p className="text-2xl font-bold font-mono">₹{totals.totalDiscountValue.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Freebies Value</p>
              <p className="text-2xl font-bold font-mono">₹{totals.freebiesValue.toFixed(2)}</p>
            </div>
          </div>

          <div className={`p-4 rounded-lg border-2 ${
            !slabRequiresApproval
              ? "bg-success/10 border-success"
              : "bg-destructive/10 border-destructive"
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {!slabRequiresApproval ? (
                  <CheckCircle2 className="h-5 w-5 text-success" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-destructive" />
                )}
                <div>
                  <p className="font-semibold">Overall Effective Discount: <span className="font-mono">{totals.effectiveDiscount.toFixed(2)}%</span></p>
                  <p className="text-xs text-muted-foreground">
                    Informational only · Approval is determined brand-wise via Slab Intelligence
                    {slabInfo.quarterLabel ? ` (${slabInfo.quarterLabel})` : ""}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-lg font-bold ${
                  !slabRequiresApproval ? "text-success" : "text-destructive"
                }`}>
                  {slabRequiresApproval ? "Manager Approval Required" : "Within Limit"}
                </p>
              </div>
            </div>
          </div>

          {/* Brand-wise Effective Discount + Slab Eligibility (Slab Intelligence-driven) */}
          {slabVal.brands.length > 0 && (
            <div className="p-4 bg-slate-50 rounded-lg border" data-testid="edit-brand-wise-discount">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
                <p className="font-semibold text-sm">Brand-wise Effective Discount &amp; Slab Eligibility</p>
                {slabInfo.quarterLabel && (
                  <span className="text-xs text-muted-foreground">
                    Slab: {slabInfo.quarterLabel}
                    {slabVal.applicableSlab?.range_label ? ` · ${slabVal.applicableSlab.range_label}` : " · No matching slab"}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {slabVal.brands.map((b) => {
                  const bad = b.status === "approval";
                  return (
                    <div
                      key={b.brand}
                      className={`p-3 rounded border-2 ${bad ? "bg-red-50 border-red-400" : "bg-green-50 border-green-400"}`}
                      data-testid={`edit-brand-stat-${b.brand}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-[#0F0F0F]">{b.brand}</p>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${bad ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}
                          data-testid={`edit-brand-status-${b.brand}`}
                        >
                          {bad ? "Approval Required" : "Within Limit"}
                        </span>
                      </div>
                      <div className="mt-1.5 space-y-0.5 text-xs">
                        <p className="flex justify-between"><span className="text-muted-foreground">Contribution</span><span className="font-mono font-medium">{b.contribution.toFixed(2)}%</span></p>
                        {b.hasContributionRule && (
                          <p className="flex justify-between"><span className="text-muted-foreground">Required Contribution</span><span className="font-mono">{b.requiredRangeLabel || "—"}</span></p>
                        )}
                        <p className="flex justify-between"><span className="text-muted-foreground">Effective Discount</span><span className="font-mono font-medium">{b.effectiveDiscount.toFixed(2)}%</span></p>
                        <p className="flex justify-between"><span className="text-muted-foreground">Allowed Discount</span><span className={`font-mono font-medium ${bad ? "text-red-600" : "text-green-700"}`}>{b.allowedLabel}</span></p>
                      </div>
                      {bad && (
                        <p className="mt-1.5 text-[11px] text-red-600 leading-tight" data-testid={`edit-brand-reason-${b.brand}`}>{b.reason}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Seller Business Contribution (Phase 1 — calculation + visibility only) */}
          <SellerContributionCard
            sellerId={selectedSellerId}
            month={orderDate ? orderDate.slice(0, 7) : ""}
            currentQuotationValue={totals.totalInvoiceValue}
            onValidationChange={(v) => setContribExceeds(!!v.exceeds)}
          />

          {/* Remarks block — always visible. Mandatory when approval is required. */}
          <div className="space-y-4 p-4 bg-slate-100 rounded-lg">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_approval_remarks" className="text-sm font-medium">
                  Remarks
                  {slabDiscountOver ? (
                    <span className="text-red-600"> *</span>
                  ) : (
                    <span className="ml-2 text-[10px] font-normal text-muted-foreground">(optional)</span>
                  )}
                </Label>
                <Textarea
                  id="edit_approval_remarks"
                  value={approvalRemarks}
                  onChange={(e) => setApprovalRemarks(e.target.value)}
                  placeholder={slabDiscountOver
                    ? "Enter the reason this quotation needs manager approval..."
                    : "Add notes about this quotation (optional)…"}
                  className="min-h-[80px]"
                  data-testid="edit-approval-remarks-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Remark Categories
                  {slabDiscountOver ? (
                    <>
                      <span className="text-red-600"> *</span>
                      <span className="ml-2 text-[10px] font-normal text-muted-foreground">(at least one)</span>
                    </>
                  ) : (
                    <span className="ml-2 text-[10px] font-normal text-muted-foreground">(optional · multi-select)</span>
                  )}
                </Label>
                <MultiSelectChips
                  options={allRemarkCategories}
                  value={remarkCategories}
                  onChange={setRemarkCategories}
                  placeholder="Pick one or more categories"
                  required={slabDiscountOver}
                  testId="edit-quotation-remark-categories"
                />
              </div>
            </div>
          </div>

          {approvalRequired && quotation && !quotation.is_draft && (
            <div className="p-4 rounded-lg border-2 border-amber-400 bg-amber-50" data-testid="edit-final-blocked-banner">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-900">Manager Approval Required</p>
                  <ul className="text-amber-800 mt-1 list-disc pl-4 space-y-0.5">
                    {slabVal.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    {contribExceeds && <li>Seller Revenue Contribution exceeds the allowed limit.</li>}
                  </ul>
                  <p className="text-amber-800 mt-1 font-medium">Click <b>Save as Draft</b> to move this quotation to Drafts (same Quotation ID) and submit it for Manager Approval.</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
